import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Instagram, Facebook, Menu, X, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const Navbar = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useAuth();

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 py-4 px-4 lg:px-8"
      style={{
        backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
      }}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo - 1st Ave ICP with original colors */}
        <Link to="/" className="flex-shrink-0">
          <img
            src="/images/misc/logo-original.png"
            alt="1st Ave ICP - Ice Cream & Pastry"
            className="h-16 lg:h-20 w-auto object-contain"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-4">
          <div className="bg-white rounded-full px-6 py-3 flex items-center gap-6 shadow-md">
            <Link
              to="/"
              className={`font-semibold transition-colors ${
                isActive('/') ? 'text-[#4A9A8D]' : 'text-gray-700 hover:text-[#4A9A8D]'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Home
            </Link>
            <Link
              to="/menu"
              className={`font-semibold transition-colors ${
                isActive('/menu') ? 'text-[#E91E8C]' : 'text-gray-700 hover:text-[#E91E8C]'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Menu
            </Link>
            <Link
              to="/specials"
              className={`font-semibold transition-colors ${
                isActive('/specials') ? 'text-[#E91E8C]' : 'text-gray-700 hover:text-[#E91E8C]'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Specials
            </Link>
            <Link
              to="/book-event"
              className={`font-semibold transition-colors ${
                isActive('/book-event') ? 'text-[#E91E8C]' : 'text-gray-700 hover:text-[#E91E8C]'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Book Event
            </Link>
            <Link
              to="/find-us"
              className={`font-semibold transition-colors ${
                isActive('/find-us') ? 'text-[#E91E8C]' : 'text-gray-700 hover:text-[#E91E8C]'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Find Us
            </Link>
            <Link
              to="/reviews"
              className={`font-semibold transition-colors ${
                isActive('/reviews') ? 'text-[#E91E8C]' : 'text-gray-700 hover:text-[#E91E8C]'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Reviews
            </Link>
            <Link
              to="/blog"
              className={`font-semibold transition-colors ${
                location.pathname.startsWith('/blog') ? 'text-[#E91E8C]' : 'text-gray-700 hover:text-[#E91E8C]'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="navbar-blog-link"
            >
              The Scoop
            </Link>
            <Link
              to="/contact"
              className={`font-semibold transition-colors ${
                isActive('/contact') ? 'text-[#E91E8C]' : 'text-gray-700 hover:text-[#E91E8C]'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Contact Us
            </Link>
            <div className="flex items-center gap-3 ml-2">
              <a
                href="https://www.instagram.com/1staveicp/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#E91E8C] hover:text-[#C4197A] transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61559570946841"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#E91E8C] hover:text-[#C4197A] transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.yelp.com/biz/1st-avenue-icp-mineral-wells?osq=1st+avenue+icp"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#FF1A1A] hover:text-[#CC0000] transition-colors"
                title="Review us on Yelp"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.16 12.594l-4.995 1.433c-.96.276-1.74-.8-1.176-1.63l2.905-4.308a1.072 1.072 0 0 1 1.596-.206 9.194 9.194 0 0 1 2.364 3.252 1.073 1.073 0 0 1-.694 1.459zm-3.965 5.985a1.072 1.072 0 0 1-.94-.38 9.194 9.194 0 0 1-1.573-3.653 1.073 1.073 0 0 1 .865-1.283l5.126-.922c1.001-.18 1.676.996 1.02 1.78l-3.378 4.037c-.22.264-.571.42-.94.42h-.18zm-5.08-4.128l-4.15 3.118c-.827.62-1.895-.202-1.615-1.244l1.47-5.075a1.072 1.072 0 0 1 1.477-.684c1.2.536 2.25 1.342 3.056 2.34.487.604.345 1.482-.238 1.865zm.296-4.466l1.83-4.883c.37-.98 1.738-.98 2.068.02l1.71 4.903c.3.86-.55 1.69-1.33 1.3l-3.54-1.77c-.75-.375-.987-1.238-.738-1.57zm-6.28 10.93a1.072 1.072 0 0 1-.32-1.49l2.762-4.4c.55-.876 1.884-.56 2.015.478l.67 5.256c.13 1.015-1.002 1.7-1.828 1.1l-3.3-2.944z"/>
                </svg>
              </a>
            </div>
          </div>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-[#E91E8C] to-[#C4197A] hover:from-[#C4197A] hover:to-[#A01565] text-white font-bold px-6 py-5 rounded-full shadow-md">
              Contact Now
            </Button>
          </Link>
          {/* Admin Dashboard Link - only visible when logged in */}
          {user && (
            <Link to="/admin/dashboard">
              <Button 
                variant="outline" 
                className="border-2 border-purple-500 text-purple-600 hover:bg-purple-500 hover:text-white font-bold px-4 py-5 rounded-full shadow-md flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Dashboard
              </Button>
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden text-[#E91E8C] p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden mt-4 bg-white rounded-2xl shadow-xl p-6">
          <div className="flex flex-col gap-4">
            <Link
              to="/"
              className={`font-semibold py-2 transition-colors ${
                isActive('/') ? 'text-[#4A9A8D]' : 'text-gray-700'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/menu"
              className={`font-semibold py-2 transition-colors ${
                isActive('/menu') ? 'text-[#E91E8C]' : 'text-gray-700'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
              onClick={() => setMobileMenuOpen(false)}
            >
              Menu
            </Link>
            <Link
              to="/specials"
              className={`font-semibold py-2 transition-colors ${
                isActive('/specials') ? 'text-[#E91E8C]' : 'text-gray-700'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
              onClick={() => setMobileMenuOpen(false)}
            >
              Specials
            </Link>
            <Link
              to="/book-event"
              className={`font-semibold py-2 transition-colors ${
                isActive('/book-event') ? 'text-[#E91E8C]' : 'text-gray-700'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
              onClick={() => setMobileMenuOpen(false)}
            >
              Book Event
            </Link>
            <Link
              to="/find-us"
              className={`font-semibold py-2 transition-colors ${
                isActive('/find-us') ? 'text-[#E91E8C]' : 'text-gray-700'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
              onClick={() => setMobileMenuOpen(false)}
            >
              Find Us
            </Link>
            <Link
              to="/reviews"
              className={`font-semibold py-2 transition-colors ${
                isActive('/reviews') ? 'text-[#E91E8C]' : 'text-gray-700'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
              onClick={() => setMobileMenuOpen(false)}
            >
              Reviews
            </Link>
            <Link
              to="/blog"
              className={`font-semibold py-2 transition-colors ${
                location.pathname.startsWith('/blog') ? 'text-[#E91E8C]' : 'text-gray-700'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
              onClick={() => setMobileMenuOpen(false)}
            >
              The Scoop
            </Link>
            <Link
              to="/contact"
              className={`font-semibold py-2 transition-colors ${
                isActive('/contact') ? 'text-[#E91E8C]' : 'text-gray-700'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact Us
            </Link>
            <div className="flex items-center gap-4 py-2">
              <a href="https://www.instagram.com/1staveicp/" target="_blank" rel="noopener noreferrer" className="text-[#E91E8C]">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/profile.php?id=61559570946841" target="_blank" rel="noopener noreferrer" className="text-[#E91E8C]">
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.yelp.com/biz/1st-avenue-icp-mineral-wells?osq=1st+avenue+icp" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-[#FF1A1A]"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.16 12.594l-4.995 1.433c-.96.276-1.74-.8-1.176-1.63l2.905-4.308a1.072 1.072 0 0 1 1.596-.206 9.194 9.194 0 0 1 2.364 3.252 1.073 1.073 0 0 1-.694 1.459zm-3.965 5.985a1.072 1.072 0 0 1-.94-.38 9.194 9.194 0 0 1-1.573-3.653 1.073 1.073 0 0 1 .865-1.283l5.126-.922c1.001-.18 1.676.996 1.02 1.78l-3.378 4.037c-.22.264-.571.42-.94.42h-.18zm-5.08-4.128l-4.15 3.118c-.827.62-1.895-.202-1.615-1.244l1.47-5.075a1.072 1.072 0 0 1 1.477-.684c1.2.536 2.25 1.342 3.056 2.34.487.604.345 1.482-.238 1.865zm.296-4.466l1.83-4.883c.37-.98 1.738-.98 2.068.02l1.71 4.903c.3.86-.55 1.69-1.33 1.3l-3.54-1.77c-.75-.375-.987-1.238-.738-1.57zm-6.28 10.93a1.072 1.072 0 0 1-.32-1.49l2.762-4.4c.55-.876 1.884-.56 2.015.478l.67 5.256c.13 1.015-1.002 1.7-1.828 1.1l-3.3-2.944z"/>
                </svg>
              </a>
            </div>
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
              <Button className="bg-gradient-to-r from-[#E91E8C] to-[#C4197A] text-white font-bold py-3 rounded-full w-full">
                Contact Now
              </Button>
            </Link>
            {/* Admin Dashboard Link - only visible when logged in */}
            {user && (
              <Link to="/admin/dashboard" onClick={() => setMobileMenuOpen(false)}>
                <Button 
                  variant="outline" 
                  className="border-2 border-purple-500 text-purple-600 font-bold py-3 rounded-full w-full flex items-center justify-center gap-2 mt-2"
                >
                  <Settings className="w-4 h-4" />
                  Admin Dashboard
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
